import { cn } from "@/lib/utils";
import type { ButtonHTMLAttributes } from "react";

// =============================================================================
// Button — variantes Apple (HIG 9.8): prominent/tinted/plain/gray/destructive.
// Compat: variantes antigas (primary/secondary/ghost/outline) continuam
// aceitas e mapeiam pra cima, pra não quebrar chamadas existentes.
// =============================================================================

type Variant =
  | "prominent"
  | "tinted"
  | "plain"
  | "gray"
  | "destructive"
  | "destructiveTinted"
  // legado — mapeiam pras variantes Apple acima
  | "primary"
  | "secondary"
  | "outline"
  | "ghost";
type Size = "sm" | "md";

const VARIANT_ALIAS: Record<Variant, Exclude<Variant, "primary" | "secondary" | "outline" | "ghost">> = {
  destructiveTinted: "destructiveTinted",
  prominent: "prominent",
  tinted: "tinted",
  plain: "plain",
  gray: "gray",
  destructive: "destructive",
  primary: "prominent",
  secondary: "gray",
  outline: "tinted",
  ghost: "plain",
};

const variantStyles: Record<
  Exclude<Variant, "primary" | "secondary" | "outline" | "ghost">,
  string
> = {
  prominent: "bg-accent text-white font-semibold hover:opacity-90 disabled:opacity-50",
  tinted: "bg-accent/14 text-accent font-medium hover:bg-accent/20 disabled:opacity-50",
  plain: "bg-transparent text-accent font-medium hover:bg-accent/8 disabled:opacity-50",
  gray: "bg-tone/8 text-text-primary font-medium hover:bg-tone/12 disabled:opacity-50",
  destructive: "bg-accent-rose text-white font-semibold hover:opacity-90 disabled:opacity-50",
  destructiveTinted: "bg-accent-rose/14 text-accent-rose font-medium hover:bg-accent-rose/20 disabled:opacity-50",
};

const sizeStyles: Record<Size, string> = {
  sm: "h-7 px-3 text-xs md:h-7",
  md: "h-8 px-4 text-sm min-h-11 md:h-8 md:min-h-0",
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

/**
 * Classes do botão para quem precisa do MESMO visual em outro elemento — tipicamente um `<Link>`:
 * `<Link className={buttonClassName({ variant: "tinted", size: "sm" })}>` em vez de `<Link><Button/></Link>`
 * (um <button> dentro de <a> é HTML inválido: dois elementos interativos aninhados, foco e leitor de
 * tela confusos).
 */
export function buttonClassName({
  variant = "gray",
  size = "md",
  className,
}: {
  variant?: Variant;
  size?: Size;
  className?: string;
} = {}): string {
  const resolved = VARIANT_ALIAS[variant];
  return cn(
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[8px] transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/50 disabled:cursor-not-allowed",
    variantStyles[resolved],
    sizeStyles[size],
    className,
  );
}

export function Button({
  variant = "gray",
  size = "md",
  className,
  children,
  ...props
}: Props) {
  return (
    <button className={buttonClassName({ variant, size, className })} {...props}>
      {children}
    </button>
  );
}
